/**
 * RolesPage — Users → Roles listing (May 2026 Figma).
 *
 * Toolbar (search icon, "Roles" + N TOTAL, overflow, + Add role, view toggle)
 * plus a table with columns:
 *   [ ] ROLE NAME ↑   SITES (link)   PERMISSIONS   BODY CAMERA USER   actions
 *
 * Backed by /api/orgs/{orgId}/employee-roles (employeeRoleService).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import employeeRoleService from '../services/employeeRoleService';
import RoleModal from '../components/users/RoleModal';

function roleName(r) {
  return r.roleName || r.name || `Role ${r.roleId ?? r.id}`;
}
function permissionCount(r) {
  if (Array.isArray(r.permissions)) return r.permissions.length;
  if (typeof r.permissionCount === 'number') return r.permissionCount;
  if (r.uiPermissionMap && typeof r.uiPermissionMap === 'object') {
    return Object.values(r.uiPermissionMap).filter((v) => {
      const normalized = String(v ?? '').trim().toUpperCase();
      return normalized && normalized !== 'NONE';
    }).length;
  }
  return 0;
}
function sitesLabel(r, totalSites) {
  if (String(r.siteAssociationMode || '').toUpperCase() === 'DYNAMIC') return 'User Sites';
  if (r.allSites === true || r.appliesToAllSites === true) return 'All sites';
  const list = r.siteIds || r.sites || [];
  if (!list.length) return '-';
  if (totalSites && list.length === totalSites) return 'All sites';
  return `${list.length} sites`;
}
function isBodyCamUser(r) {
  return r.bodyCameraUser === true || r.isBodyCameraUser === true;
}

export default function RolesPage() {
  const { orgId, sites: tenantSites } = useTenant();
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery]       = useState('');
  const [sort, setSort]         = useState({ key: 'name', asc: true });
  const [selected, setSelected] = useState(() => new Set());
  const [deletingId, setDeletingId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [showAdd, setShowAdd]       = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const refresh = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    if (!orgId) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    employeeRoleService.listRoles(orgId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.content || res?.items || []);
        setRows(list);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.message ?? e.message ?? 'Failed to load roles'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, refreshTick]);

  const totalSites = Array.isArray(tenantSites) ? tenantSites.length : 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = !q ? rows : rows.filter((r) => roleName(r).toLowerCase().includes(q));
    const get = (r) => {
      switch (sort.key) {
        case 'name':        return roleName(r).toLowerCase();
        case 'sites':       return (r.siteIds || r.sites || []).length;
        case 'permissions': return permissionCount(r);
        case 'bodyCam':     return isBodyCamUser(r) ? 1 : 0;
        default:            return 0;
      }
    };
    return [...base].sort((a, b) => {
      const x = get(a), y = get(b);
      if (x === y) return 0;
      return (sort.asc ? 1 : -1) * (x < y ? -1 : 1);
    });
  }, [rows, query, sort]);

  const allChecked  = filtered.length > 0 && filtered.every((r) => selected.has(r.roleId ?? r.id));
  const someChecked = !allChecked && filtered.some((r) => selected.has(r.roleId ?? r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = (r) => r.roleId ?? r.id;
      if (allChecked) filtered.forEach((r) => next.delete(key(r)));
      else            filtered.forEach((r) => next.add(key(r)));
      return next;
    });
  };
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));

  const handleEdit = (r) => {
    setEditingRole(r);
  };
  const handleAdd = () => {
    setShowAdd(true);
  };
  const handleDelete = async (r) => {
    const id = r.roleId ?? r.id;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete role "${roleName(r)}"? This cannot be undone.`)) return;
    setDeletingId(id);
    setError(null);
    try {
      await employeeRoleService.deleteRole(id, orgId);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to delete role');
    } finally {
      setDeletingId(null);
    }
  };

  const ThSort = ({ id, label }) => (
    <th>
      <button className="data-table-sort" onClick={() => toggleSort(id)}>
        {label}
        <i className={`fas fa-sort${sort.key === id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
      </button>
    </th>
  );

  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search roles"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>Roles</h1>
            <div className="sp-subhead"><strong>{filtered.length}</strong> TOTAL</div>
            {searchOpen && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search roles..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add role
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {error && <div className="users-error">{error}</div>}

      <table className="data-table cm-table sp-table">
        <thead>
          <tr>
            <th className="cm-col-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                onChange={toggleAll}
                aria-label="Select all roles"
              />
            </th>
            <ThSort id="name"        label="ROLE NAME" />
            <ThSort id="sites"       label="SITES" />
            <ThSort id="permissions" label="PERMISSIONS" />
            <ThSort id="bodyCam"     label="BODY CAMERA USER" />
            <th className="cm-col-actions" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>
              <i className="fas fa-spinner fa-spin" /> Loading…
            </td></tr>
          )}
          {!loading && filtered.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
              No roles found.
            </td></tr>
          )}
          {!loading && filtered.map((r) => {
            const id = r.roleId ?? r.id;
            const sitesText = sitesLabel(r, totalSites);
            const isLink = sitesText !== '-';
            return (
              <tr key={id}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(id)}
                    onChange={() => toggleRow(id)}
                    aria-label={`Select ${roleName(r)}`}
                  />
                </td>
                <td className="up-cell-name">{roleName(r)}</td>
                <td>
                  {isLink
                    ? <span className="up-sites-link">{sitesText}</span>
                    : <span className="up-muted">-</span>}
                </td>
                <td>{permissionCount(r)}</td>
                <td>{isBodyCamUser(r) ? 'Yes' : 'No'}</td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit role"
                    aria-label="Edit role"
                    onClick={() => handleEdit(r)}
                    disabled={deletingId === id}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Delete role"
                    aria-label="Delete role"
                    onClick={() => handleDelete(r)}
                    disabled={deletingId === id}
                  >
                    <i className={`fas ${deletingId === id ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <RoleModal
        open={showAdd || !!editingRole}
        role={editingRole}
        onClose={() => { setShowAdd(false); setEditingRole(null); }}
        onSaved={() => { refresh(); }}
      />
    </>
  );
}

